import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Search, Star } from 'lucide-react';
import iconsService from '../../services/iconsService';
import { categoriesService } from '../../services/categoriesService';
import { brandsService } from '../../services/brandsService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FiltrVyber } from '../../components/ui/FiltrVyber';

// Zvláštní volby ve filtru: ikona bez kategorie/značky je univerzální a nabízí
// se u všech produktů. Po převzetí kategorií ze starého systému je potřeba umět
// takové ikony najít – proto vlastní položka, ne jen výčet číselníku.
const BEZ_KATEGORIE = '__bez__';
const BEZ_ZNACKY = '__bez__';

const TYPY = [
  { id: 'energy', label: 'Energetická třída' },
  { id: 'bezna', label: 'Běžná' },
];

export const IconsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const [kategorie, setKategorie] = useState<string[]>([]);
  const [znacky, setZnacky] = useState<string[]>([]);
  const [typ, setTyp] = useState<string[]>([]);

  const { data: icons = [], isLoading } = useQuery({
    queryKey: ['icons'],
    queryFn: () => iconsService.getAllIcons(),
  });

  // Číselníky do nabídek – vypisují se všechny, ne jen ty použité u ikon.
  const { data: vsechnyKategorie = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesService.getAllCategories(),
  });
  const { data: vsechnyZnacky = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: () => brandsService.getAllBrands(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => iconsService.deleteIcon(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['icons'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Chyba při mazání ikony';
      alert(errorMessage);
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Opravdu chcete smazat ikonu "${name}"?`)) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const nabidkaKategorii = useMemo(
    () => [
      { id: BEZ_KATEGORIE, label: 'Bez kategorie' },
      ...vsechnyKategorie.map((k: any) => ({ id: k.id, label: k.name })),
    ],
    [vsechnyKategorie],
  );

  const nabidkaZnacek = useMemo(
    () => [
      { id: BEZ_ZNACKY, label: 'Bez značky' },
      ...vsechnyZnacky.map((z: any) => ({ id: z.id, label: z.name })),
    ],
    [vsechnyZnacky],
  );

  // Uvnitř jednoho filtru platí „nebo" (stačí jedna vybraná kategorie),
  // mezi filtry „a zároveň". Prázdný výběr = bez omezení.
  const filteredIcons = useMemo(() => {
    const dotaz = search.trim().toLowerCase();

    return icons.filter((icon: any) => {
      if (dotaz && !icon.name.toLowerCase().includes(dotaz)) return false;

      if (kategorie.length) {
        const vlastni: string[] = (icon.categories ?? []).map((ic: any) => ic.category.id);
        const sedne = kategorie.some((k) =>
          k === BEZ_KATEGORIE ? vlastni.length === 0 : vlastni.includes(k),
        );
        if (!sedne) return false;
      }

      if (znacky.length) {
        const vlastni: string[] = (icon.brands ?? []).map((ib: any) => ib.brand.id);
        const sedne = znacky.some((z) =>
          z === BEZ_ZNACKY ? vlastni.length === 0 : vlastni.includes(z),
        );
        if (!sedne) return false;
      }

      if (typ.length) {
        const mujTyp = icon.isEnergyClass ? 'energy' : 'bezna';
        if (!typ.includes(mujTyp)) return false;
      }

      return true;
    });
  }, [icons, search, kategorie, znacky, typ]);

  const filtrujeSe = Boolean(search) || kategorie.length > 0 || znacky.length > 0 || typ.length > 0;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Ikony</h1>
          <p className="mt-2 text-gray-600">Spravujte ikony pro produkty</p>
        </div>
        <Button onClick={() => navigate('/admin/icons/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nová ikona
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Hledat podle názvu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        {/* Nízké filtry vpravo pod hledáním – jako u promo obrázků, jen s rozbalovací
            nabídkou, protože kategorií a značek je na řadu tlačítek moc. */}
        <div className="flex flex-wrap items-center justify-end gap-1 mt-1">
          <span className="text-xs text-gray-500 mr-1">
            {filteredIcons.length} z {icons.length}
          </span>
          <FiltrVyber
            nazev="Kategorie"
            polozky={nabidkaKategorii}
            vybrane={kategorie}
            onZmena={setKategorie}
          />
          <FiltrVyber nazev="Značky" polozky={nabidkaZnacek} vybrane={znacky} onZmena={setZnacky} />
          <FiltrVyber nazev="Typ" polozky={TYPY} vybrane={typ} onZmena={setTyp} jenJedna />
          {filtrujeSe && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setKategorie([]);
                setZnacky([]);
                setTyp([]);
              }}
              className="px-2 py-0.5 rounded-full border border-gray-300 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Vymazat filtry
            </button>
          )}
        </div>
      </div>

      {filteredIcons.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Star className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné ikony nenalezeny</h3>
          <p className="text-gray-600 mb-6">
            {filtrujeSe ? 'Zkuste upravit vyhledávání nebo filtry' : 'Začněte vytvořením první ikony'}
          </p>
          {!filtrujeSe && (
            <Button onClick={() => navigate('/admin/icons/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Vytvořit ikonu
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Náhled
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Název
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategorie
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Značky
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Typ
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredIcons.map((icon: any) => (
                <tr key={icon.id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div
                      className={`rounded p-1 inline-block ${icon.useBrandColor ? 'bg-gray-300' : 'bg-gray-100'}`}
                      title={icon.useBrandColor ? 'Ikona se v letáku podbarvuje barvou značky' : undefined}
                    >
                      <img
                        src={icon.imageUrl}
                        alt={icon.name}
                        className="h-8 w-auto object-contain"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 max-w-[14rem] truncate" title={icon.name}>{icon.name}</div>
                  </td>
                  <td className="px-3 py-4">
                    <div
                      className="text-sm text-gray-500 max-w-[10rem] truncate"
                      title={icon.categories?.map((ic: any) => ic.category.name).join(', ')}
                    >
                      {icon.categories && icon.categories.length > 0
                        ? icon.categories.map((ic: any) => ic.category.name).join(', ')
                        : '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div
                      className="text-sm text-gray-500 max-w-[10rem] truncate"
                      title={icon.brands?.map((ib: any) => ib.brand.name).join(', ')}
                    >
                      {icon.brands && icon.brands.length > 0
                        ? icon.brands.map((ib: any) => ib.brand.name).join(', ')
                        : '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      icon.isEnergyClass ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {icon.isEnergyClass ? 'Energetická třída' : 'Běžná'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-0.5">
                      <button
                        onClick={() => navigate(`/admin/icons/${icon.id}/edit`)}
                        title="Upravit ikonu"
                        className="text-blue-600 hover:text-blue-900 p-1.5 rounded hover:bg-blue-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(icon.id, icon.name)}
                        title="Smazat ikonu"
                        className="text-red-600 hover:text-red-900 p-1.5 rounded hover:bg-red-50"
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
